import { StrictMode, Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import App from './App';
import AuthGuard from './components/AuthGuard';
import { EntitlementProvider } from './utils/entitlement';
import './index.css';

// Lazy load components for code splitting
const Login = lazy(() => import('./components/Login'));
const Registration = lazy(() => import('./components/Registration'));
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const Settings = lazy(() => import('./components/Settings'));
const OfficeManagement = lazy(() => import('./components/OfficeManagement'));
const OfficeDashboard = lazy(() => import('./components/OfficeDashboard'));
const MetaCallback = lazy(() => import('./components/MetaCallback'));
const DownloadDist = lazy(() => import('./components/DownloadDist'));
const NetworkDashboard = lazy(() => import('./components/NetworkDashboard'));
const DeveloperDashboard = lazy(() => import('./components/DeveloperDashboard'));
const DeveloperGate = lazy(() => import('./components/DeveloperGate'));
const DeveloperProjects = lazy(() => import('./components/DeveloperProjects'));
const DeveloperProjectDetail = lazy(() => import('./components/DeveloperProjectDetail'));
const DeveloperUnits = lazy(() => import('./components/DeveloperUnits'));
const DeveloperUnitDetail = lazy(() => import('./components/DeveloperUnitDetail'));
const InviteLanding = lazy(() => import('./components/InviteLanding'));

const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <AuthGuard>
        <App />
      </AuthGuard>
    ),
  },
  {
    path: '/login',
    element: (
      <Suspense fallback={<div className="flex items-center justify-center min-h-screen">Loading...</div>}>
        <Login />
      </Suspense>
    ),
  },
  {
    path: '/register',
    element: (
      <Suspense fallback={<div className="flex items-center justify-center min-h-screen">Loading...</div>}>
        <Registration />
      </Suspense>
    ),
  },
  {
    path: '/forgot-password',
    element: (
      <Suspense fallback={<div className="flex items-center justify-center min-h-screen">Loading...</div>}>
        <ForgotPassword />
      </Suspense>
    ),
  },
  {
    path: '/settings',
    element: (
      <AuthGuard>
        <Suspense fallback={<div className="flex items-center justify-center min-h-screen">Loading...</div>}>
          <Settings />
        </Suspense>
      </AuthGuard>
    ),
  },
  {
    path: '/office',
    element: (
      <AuthGuard>
        <Suspense fallback={<div className="flex items-center justify-center min-h-screen">Loading...</div>}>
          <OfficeManagement />
        </Suspense>
      </AuthGuard>
    ),
  },
  {
    path: '/dashboard',
    element: (
      <AuthGuard>
        <Suspense fallback={<div className="flex items-center justify-center min-h-screen">Loading...</div>}>
          <OfficeDashboard />
        </Suspense>
      </AuthGuard>
    ),
  },
  {
    path: '/meta/callback',
    element: (
      <AuthGuard>
        <Suspense fallback={<div className="flex items-center justify-center min-h-screen">Loading...</div>}>
          <MetaCallback />
        </Suspense>
      </AuthGuard>
    ),
  },
  {
    path: '/download-dist',
    element: (
      <Suspense fallback={<div className="flex items-center justify-center min-h-screen">Loading...</div>}>
        <DownloadDist />
      </Suspense>
    ),
  },
  {
    path: '/invite/:token',
    element: (
      <Suspense fallback={<div className="flex items-center justify-center min-h-screen">Loading...</div>}>
        <InviteLanding />
      </Suspense>
    ),
  },
  {
    path: '/network',
    element: (
      <AuthGuard>
        <Suspense fallback={<div className="flex items-center justify-center min-h-screen">Loading...</div>}>
          <NetworkDashboard onClose={() => { window.history.back(); }} />
        </Suspense>
      </AuthGuard>
    ),
  },
  {
    path: '/developer',
    element: (
      <AuthGuard>
        <Suspense fallback={<div className="flex items-center justify-center min-h-screen">Loading...</div>}>
          <DeveloperGate>
            <DeveloperDashboard />
          </DeveloperGate>
        </Suspense>
      </AuthGuard>
    ),
  },
  {
    path: '/developer/projects',
    element: (
      <AuthGuard>
        <Suspense fallback={<div className="flex items-center justify-center min-h-screen">Loading...</div>}>
          <DeveloperGate>
            <DeveloperProjects />
          </DeveloperGate>
        </Suspense>
      </AuthGuard>
    ),
  },
  {
    path: '/developer/projects/:projectId',
    element: (
      <AuthGuard>
        <Suspense fallback={<div className="flex items-center justify-center min-h-screen">Loading...</div>}>
          <DeveloperGate>
            <DeveloperProjectDetail />
          </DeveloperGate>
        </Suspense>
      </AuthGuard>
    ),
  },
  {
    path: '/developer/projects/:projectId/units',
    element: (
      <AuthGuard>
        <Suspense fallback={<div className="flex items-center justify-center min-h-screen">Loading...</div>}>
          <DeveloperGate>
            <DeveloperUnits scope="project" />
          </DeveloperGate>
        </Suspense>
      </AuthGuard>
    ),
  },
  {
    path: '/developer/projects/:projectId/units/:unitId',
    element: (
      <AuthGuard>
        <Suspense fallback={<div className="flex items-center justify-center min-h-screen">Loading...</div>}>
          <DeveloperGate>
            <DeveloperUnitDetail />
          </DeveloperGate>
        </Suspense>
      </AuthGuard>
    ),
  },
  {
    path: '/developer/units',
    element: (
      <AuthGuard>
        <Suspense fallback={<div className="flex items-center justify-center min-h-screen">Loading...</div>}>
          <DeveloperGate>
            <DeveloperUnits scope="office" />
          </DeveloperGate>
        </Suspense>
      </AuthGuard>
    ),
  },
]);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <EntitlementProvider>
      <Suspense fallback={<div className="flex items-center justify-center min-h-screen">Loading...</div>}>
        <RouterProvider router={router} />
      </Suspense>
    </EntitlementProvider>
  </StrictMode>
);